/**
 * TestCaseList — middle pane of the three-pane layout.
 *
 * Normal mode:
 *   Renders the list using @tanstack/react-virtual for O(1) DOM nodes.
 *   Each row can be clicked (opens detail pane) or checkbox-selected.
 *   The grip handle on each row lets users drag rows to folders.
 *
 * Reorder mode (toggled via the "Reorder" button):
 *   Virtualization is disabled so @dnd-kit/sortable's SortableContext can
 *   manage the list safely. The whole row becomes a drag handle. Clicking
 *   and checkbox selection are disabled to prevent accidental activations.
 *   Exiting reorder mode re-enables virtualization.
 */

import { useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader2, AlertCircle, Plus, ArrowUpDown } from 'lucide-react';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTestCasesStore } from '../../../store/useTestCasesStore';
import { TestCaseRow }      from './TestCaseRow';
import { TestCaseQuickAdd } from './TestCaseQuickAdd';
import { NEW_TEST_CASE_ID } from '../types';
import type { ITestCaseRow } from '../types';

const ROW_HEIGHT_PX = 56;

interface TestCaseListProps {
    testCases:    ITestCaseRow[];
    isLoading:    boolean;
    isError:      boolean;
    projectId:    string | null;
    folderId?:    string | null;
    onInvalidate: () => void;
    /** ID of the test case row currently being hovered during a reorder drag. */
    dragOverId?:   string | null;
    /** Which edge of the hovered row the dragged item will be inserted at. */
    dropPosition?: 'before' | 'after' | null;
    /** When true, reorder mode is active: virtualization disabled, whole row is draggable. */
    isReorderMode:       boolean;
    onToggleReorderMode: () => void;
}

// ── SortableTestCaseRow ──────────────────────────────────────────────────────
// Thin sortable wrapper used only in reorder mode. Keeps useSortable out of
// TestCaseRow so there are no hook conflicts with useDraggable/useDroppable.
function SortableTestCaseRow({ testCase }: { testCase: ITestCaseRow }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id:   testCase._id,
        data: { type: 'testCase', label: testCase.tcId ?? testCase.title },
    });

    return (
        <div
            ref={setNodeRef}
            style={{
                transform:  CSS.Transform.toString(transform),
                transition,
                opacity:    isDragging ? 0.4 : 1,
                zIndex:     isDragging ? 10 : undefined,
            }}
            {...attributes}
            {...listeners}
        >
            <TestCaseRow testCase={testCase} isReorderMode={true} />
        </div>
    );
}

// ── TestCaseList ─────────────────────────────────────────────────────────────

export function TestCaseList({
    testCases,
    isLoading,
    isError,
    projectId,
    folderId,
    onInvalidate,
    dragOverId,
    dropPosition,
    isReorderMode,
    onToggleReorderMode,
}: TestCaseListProps) {
    const setSelectedTestCaseId = useTestCasesStore((s) => s.setSelectedTestCaseId);
    const selectedIds           = useTestCasesStore((s) => s.selectedIds);
    const selectAll             = useTestCasesStore((s) => s.selectAll);
    const clearSelection        = useTestCasesStore((s) => s.clearSelection);

    const [showQuickAdd, setShowQuickAdd] = useState(false);

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Always initialise virtualizer (rules of hooks); only used in normal mode.
    const virtualizer = useVirtualizer({
        count:            testCases.length,
        getScrollElement: () => scrollContainerRef.current,
        estimateSize:     () => ROW_HEIGHT_PX,
        overscan:         10,
    });

    const allSelected  = testCases.length > 0 && testCases.every((tc) => selectedIds.has(tc._id));
    const someSelected = testCases.some((tc) => selectedIds.has(tc._id));

    function handleSelectAll() {
        if (allSelected) clearSelection();
        else             selectAll(testCases.map((tc) => tc._id));
    }

    function handleQuickAddCreated(newId: string) {
        setShowQuickAdd(false);
        onInvalidate();
        setSelectedTestCaseId(newId);
    }

    return (
        <div data-testid="test-case-list" className="flex flex-col flex-1 min-w-0 overflow-hidden">
            {/* List toolbar */}
            <div data-testid="test-case-list-toolbar" className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark flex-shrink-0">
                {/* Select-all checkbox — hidden in reorder mode */}
                {!isReorderMode && (
                    <input
                        type="checkbox"
                        aria-label={allSelected ? 'Deselect all test cases' : 'Select all test cases'}
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                        onChange={handleSelectAll}
                        className="w-3.5 h-3.5 rounded accent-blue-600 cursor-pointer"
                        title={allSelected ? 'Deselect all' : 'Select all'}
                    />
                )}

                <span className="text-xs text-slate-500 dark:text-slate-400 flex-1">
                    {isLoading ? 'Loading…' : `${testCases.length} test case${testCases.length !== 1 ? 's' : ''}`}
                </span>

                {/* Reorder mode toggle — only shown when there are rows to reorder */}
                {!isLoading && !isError && testCases.length > 1 && (
                    <button
                        data-testid="test-case-list-reorder-button"
                        onClick={onToggleReorderMode}
                        className={`hidden md:flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg transition-colors
                            ${isReorderMode
                                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 ring-1 ring-amber-400/50'
                                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                            }`}
                        title={isReorderMode ? 'Exit reorder mode' : 'Drag rows to reorder test cases'}
                    >
                        <ArrowUpDown size={12} />
                        {isReorderMode ? 'Done' : 'Reorder'}
                    </button>
                )}

                {/* Quick add button — hidden in reorder mode */}
                {projectId && !showQuickAdd && !isReorderMode && (
                    <button
                        data-testid="test-case-list-new-button"
                        onClick={() => setShowQuickAdd(true)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Quick add test case (N)"
                    >
                        <Plus size={13} />
                        New
                    </button>
                )}
            </div>

            {/* Loading / error states */}
            {isLoading && (
                <div className="flex items-center justify-center flex-1">
                    <Loader2 size={20} className="animate-spin text-slate-300" />
                </div>
            )}
            {isError && !isLoading && (
                <div className="flex items-center justify-center flex-1 gap-2 text-sm text-rose-500">
                    <AlertCircle size={16} /> Failed to load test cases
                </div>
            )}

            {/* Empty state */}
            {!isLoading && !isError && testCases.length === 0 && (
                <div className="flex flex-col items-center justify-center flex-1 gap-2 text-slate-400">
                    <p className="text-sm">No test cases in this folder</p>
                    {projectId && (
                        <button
                            onClick={() => setShowQuickAdd(true)}
                            className="text-xs text-blue-500 hover:underline"
                        >
                            + Add the first one
                        </button>
                    )}
                </div>
            )}

            {/* ── List content ─────────────────────────────────────────────── */}
            {!isLoading && !isError && testCases.length > 0 && (
                isReorderMode ? (
                    // ── Reorder mode: non-virtualized + @dnd-kit/sortable ────
                    <SortableContext
                        items={testCases.map((tc) => tc._id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <div
                            className="flex-1 overflow-y-auto"
                            role="listbox"
                            aria-label="Test cases (reorder mode)"
                        >
                            {testCases.map((tc) => (
                                <SortableTestCaseRow key={tc._id} testCase={tc} />
                            ))}
                        </div>
                    </SortableContext>
                ) : (
                    // ── Normal mode: virtualized ─────────────────────────────
                    <div
                        ref={scrollContainerRef}
                        role="listbox"
                        aria-label="Test cases"
                        aria-multiselectable="true"
                        className="flex-1 overflow-y-auto"
                    >
                        {/* Single spacer div whose height equals the full virtual list */}
                        <div
                            style={{
                                height:   `${virtualizer.getTotalSize()}px`,
                                position: 'relative',
                            }}
                        >
                            {virtualizer.getVirtualItems().map((vRow) => {
                                const tc = testCases[vRow.index];
                                return (
                                    <TestCaseRow
                                        key={tc._id}
                                        testCase={tc}
                                        style={{
                                            position:  'absolute',
                                            top:       0,
                                            transform: `translateY(${vRow.start}px)`,
                                            width:     '100%',
                                            height:    `${vRow.size}px`,
                                        }}
                                        isDropTarget={dragOverId === tc._id}
                                        dropPosition={dragOverId === tc._id ? dropPosition : null}
                                        isReorderMode={false}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )
            )}

            {/* Quick add row */}
            {showQuickAdd && projectId && (
                <TestCaseQuickAdd
                    projectId={projectId}
                    folderId={folderId}
                    onCreated={handleQuickAddCreated}
                    onCancel={() => setShowQuickAdd(false)}
                />
            )}
        </div>
    );
}
